import { useState, useEffect } from "react";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { collection, onSnapshot, doc, setDoc, addDoc, query, orderBy, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase";

// Config & Utils
import { BRAND, THEMES, STAFF, STAFF_COLORS } from "./config/constants";
import { getStaffName } from "./utils/helpers";
import { staffAvatar } from "./utils/styles";
import useMobile from "./hooks/useMobile";
import useSessionSecurity from "./hooks/useSessionSecurity";

// UI Components
import Icon from "./components/ui/Icon";
import NetSaveLogo from "./components/ui/NetSaveLogo";

// Page Components
import LoginScreen from "./components/auth/LoginScreen";
import ReportsPage from "./components/dashboard/ReportsPage";
import PipelinePage from "./components/pipeline/PipelinePage";
import LeadsPage from "./components/leads/LeadsPage";
import ClientsPage from "./components/clients/ClientsPage";
import QuickBooksPage from "./components/quickbooks/QuickBooksPage";
import OneDrivePage from "./components/onedrive/OneDrivePage";
import OutlookPage from "./components/outlook/OutlookPage";
import SettingsPage from "./components/settings/SettingsPage";
import BlogPage from "./components/blog/BlogPage";
import SocialPage from "./components/social/SocialPage";
import NetSaveAI from "./components/ai/NetSaveAI";

// Inject global keyframes for spinner / bounce animations
if (!document.getElementById("netsave-global-styles")) {
  const s = document.createElement("style");
  s.id = "netsave-global-styles";
  s.textContent = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } @keyframes bounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-14px); } }`;
  document.head.appendChild(s);
}

export default function App(){
  const [theme,setTheme]=useState(()=>localStorage.getItem("ns-theme")||"dark");
  const [authUser,setAuthUser]=useState(null);
  const [authLoading,setAuthLoading]=useState(true);
  const [page,setPage]=useState("dashboard");
  const [sidebarOpen,setSidebarOpen]=useState(false);
  const [dashboardClient,setDashboardClient]=useState(null);
  const [pipelineBiz,setPipelineBiz]=useState(null);
  const [clients,setClients]=useState([]);
  const [leads,setLeads]=useState([]);
  const [dataLoading,setDataLoading]=useState(true);
  const [staffSigs,setStaffSigs]=useState({});
  const [sigSaving,setSigSaving]=useState({});
  // The AI panel is reachable from any page, so it lives here rather than in a
  // page component. `aiPanelOpen` is the slide-over; page==="ai" is the full view.
  const [aiPanelOpen,setAiPanelOpen]=useState(false);

  const mobile=useMobile();
  const { showWarning, warningSeconds, dismissWarning, logoutReason } = useSessionSecurity(authUser);
  const toggleTheme=()=>setTheme(th=>{const next=th==="dark"?"light":"dark";localStorage.setItem("ns-theme",next);return next;});
  useEffect(()=>{const u=onAuthStateChanged(auth,user=>{setAuthUser(user);setAuthLoading(false);});return u;},[]);
  useEffect(()=>{
    if(!authUser)return;
    setDataLoading(true);
    const u1=onSnapshot(query(collection(db,"clients"),orderBy("createdAt","desc")),snap=>{setClients(snap.docs.map(d=>({id:d.id,...d.data()})));setDataLoading(false);});
    const u2=onSnapshot(query(collection(db,"leads"),orderBy("createdAt","desc")),snap=>{setLeads(snap.docs.map(d=>({id:d.id,...d.data()})));});
    const u3=onSnapshot(doc(db,"settings","staffSignatures"),snap=>{if(snap.exists())setStaffSigs(snap.data());});
    return()=>{u1();u2();u3();};
  },[authUser]);

  // Hooks must be before ALL early returns
  const [myPendingTasks,setMyPendingTasks]=useState([]);
  useEffect(()=>{
    if(!authUser) return;
    const cu=getStaffName(authUser.email);
    const unsub=onSnapshot(collection(db,"tasks"),snap=>{
      const all=snap.docs.map(d=>({id:d.id,...d.data()}));
      setMyPendingTasks(all.filter(t=>t.assignedTo===cu&&t.status!=="closed"));
    });
    return unsub;
  },[authUser]);

  const t=THEMES[theme];
  if(authLoading)return <div style={{minHeight:"100vh",background:THEMES[theme].bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Outfit',sans-serif"}}><NetSaveLogo size={60} theme={theme}/></div>;
  if(!authUser){
    if(logoutReason) return (
      <div style={{minHeight:"100vh",background:THEMES[theme].bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Outfit',sans-serif"}}>
        <div style={{background:THEMES[theme].surface,border:"1px solid "+THEMES[theme].border,borderRadius:16,padding:"40px 36px",maxWidth:420,textAlign:"center",boxShadow:"0 8px 30px rgba(0,0,0,0.15)"}}>
          <div style={{fontSize:40,marginBottom:16}}>🔒</div>
          <div style={{fontSize:18,fontWeight:800,color:THEMES[theme].text,marginBottom:8}}>Session Ended</div>
          <div style={{fontSize:14,color:THEMES[theme].textMuted,marginBottom:24}}>{logoutReason}</div>
          <button onClick={()=>window.location.reload()} style={{padding:"10px 28px",borderRadius:10,background:"linear-gradient(135deg,"+BRAND.blue+",#1a8bbf)",border:"none",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>Sign In Again</button>
        </div>
      </div>
    );
    return <LoginScreen theme={theme} toggleTheme={toggleTheme}/>;
  }
  const displayName=authUser.displayName||authUser.email?.split("@")[0]||"User";
  const currentUser = getStaffName(authUser.email);

  const nav=[{id:"dashboard",label:"Dashboard",icon:"dashboard"},{id:"pipeline",label:"Pipeline",icon:"pipeline"},{id:"leads",label:"Leads",icon:"leads",badge:leads.length},{id:"clients",label:"Clients",icon:"users",badge:clients.length},{id:"ai",label:"AI",icon:"brain"},{id:"quickbooks",label:"QB",icon:"invoice",badge:clients.filter(c=>!(c.qbCustomerIds?.length>0)&&!c.qbCustomerId).length||undefined},{id:"onedrive",label:"OneDrive",icon:"file"},{id:"outlook",label:"Outlook",icon:"mail"},{id:"blog",label:"Blog",icon:"blog"},{id:"social",label:"Social",icon:"social"},{id:"settings",label:"Settings",icon:"settings"}];

  const saveSig=async(staffName,dataUrl)=>{
    setSigSaving(prev=>({...prev,[staffName]:true}));
    try{
      await setDoc(doc(db,"settings","staffSignatures"),{[staffName]:dataUrl},{merge:true});
      setStaffSigs(prev=>({...prev,[staffName]:dataUrl}));
    }catch(e){ alert("Save failed: "+e.message); }
    setSigSaving(prev=>({...prev,[staffName]:false}));
  };
  const clearSig=async(staffName)=>{
    const updated={...staffSigs};
    delete updated[staffName];
    await setDoc(doc(db,"settings","staffSignatures"),updated);
    setStaffSigs(updated);
  };

  const navigateTo=(id)=>{setPage(id);setSidebarOpen(false);};

  // Sidebar content — shared between desktop and mobile drawer
  const SidebarContent=()=>(
    <>
      <div style={{padding:"22px 18px 18px",borderBottom:`1px solid ${t.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <NetSaveLogo size={40} textMode theme={theme}/>
        {mobile&&<button onClick={()=>setSidebarOpen(false)} style={{background:"none",border:"none",cursor:"pointer",padding:4}}><Icon name="x" size={20} color={t.textSub}/></button>}
      </div>
      <div style={{padding:"16px 12px",flex:1,overflowY:"auto"}}>
        <div style={{fontSize:9.5,color:t.textMuted,letterSpacing:"0.12em",textTransform:"uppercase",padding:"0 8px",marginBottom:8}}>Menu</div>
        {nav.map(n=>{const active=page===n.id;return(
          <button key={n.id} onClick={()=>navigateTo(n.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 10px",borderRadius:8,background:active?BRAND.blue+"18":"transparent",color:active?BRAND.blue:t.textSub,fontSize:13.5,fontWeight:active?700:500,cursor:"pointer",border:`1px solid ${active?BRAND.blue+"40":"transparent"}`,width:"100%",textAlign:"left",marginBottom:2,fontFamily:"'Outfit',sans-serif"}}>
            <Icon name={n.icon} size={16} color={active?BRAND.blue:t.textSub}/>
            {n.id==="quickbooks"?"QuickBooks":n.id==="onedrive"?"OneDrive":n.id==="outlook"?"Outlook":n.label}
            {n.badge?<span style={{marginLeft:"auto",background:active?BRAND.blue:t.border,color:active?"#fff":t.textSub,fontSize:10.5,fontWeight:800,padding:"1px 7px",borderRadius:10}}>{n.badge}</span>:null}
          </button>
        );})}
      </div>
      <div style={{padding:"12px",borderTop:`1px solid ${t.border}`}}>
        <div style={{fontSize:9.5,color:t.textMuted,letterSpacing:"0.12em",textTransform:"uppercase",padding:"0 8px",marginBottom:10}}>Team</div>
        {STAFF.map(s=><div key={s} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px"}}>{staffAvatar(s,22)}<span style={{fontSize:12.5,color:t.textSub,fontWeight:500}}>{s}</span><span style={{marginLeft:"auto",fontSize:11,color:STAFF_COLORS[s],fontWeight:800}}>{clients.filter(c=>c.assignedTo===s).length}</span></div>)}
      </div>
      <div style={{padding:"12px 14px",borderTop:`1px solid ${t.border}`,display:"flex",alignItems:"center",gap:10}}>
        <div style={{width:32,height:32,borderRadius:"50%",background:`linear-gradient(135deg,${BRAND.blue},${BRAND.green})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:"#fff",flexShrink:0}}>{displayName[0]?.toUpperCase()}</div>
        <div style={{flex:1,minWidth:0}}><div style={{fontSize:12.5,fontWeight:700,color:t.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{displayName}</div><div style={{fontSize:10.5,color:t.textMuted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{authUser.email}</div></div>
        <button onClick={async()=>{try{await addDoc(collection(db,"audit_log"),{uid:authUser.uid,email:authUser.email,action:"manual_logout",timestamp:serverTimestamp()});}catch(e){}signOut(auth);}} style={{background:"none",border:"none",cursor:"pointer",padding:6,color:t.textMuted}}><Icon name="logout" size={15}/></button>
      </div>
    </>
  );

  const pageLabels={dashboard:"Overview",leads:"Lead Pipeline",clients:"Clients",quickbooks:"QuickBooks",forms:"Forms",ai:"NetSave AI"};

  return(
    <>
      {/* Inactivity warning overlay */}
      {showWarning&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Outfit',sans-serif"}} onClick={dismissWarning}>
          <div style={{background:THEMES[theme].surface,borderRadius:16,padding:"36px 32px",maxWidth:400,textAlign:"center",boxShadow:"0 12px 40px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:40,marginBottom:12}}>⏱</div>
            <div style={{fontSize:18,fontWeight:800,color:THEMES[theme].text,marginBottom:8}}>Are you still there?</div>
            <div style={{fontSize:14,color:THEMES[theme].textMuted,marginBottom:20}}>You'll be logged out in <strong style={{color:"#f59e0b",fontSize:18}}>{warningSeconds}s</strong> due to inactivity</div>
            <button onClick={dismissWarning} style={{padding:"10px 32px",borderRadius:10,background:"linear-gradient(135deg,"+BRAND.blue+",#1a8bbf)",border:"none",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>I'm still here</button>
          </div>
        </div>
      )}
      {/* NetSave AI slide-over. Not rendered until first opened, so the panel's
          session list is not fetched on every page load. */}
      {aiPanelOpen&&(
        <>
          <div onClick={()=>setAiPanelOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:300,backdropFilter:"blur(2px)"}}/>
          <div style={{position:"fixed",top:0,right:0,bottom:0,width:mobile?"100%":"min(680px,88vw)",background:t.bg,borderLeft:`1px solid ${t.border}`,zIndex:301,display:"flex",flexDirection:"column",boxShadow:"-8px 0 32px rgba(0,0,0,0.3)"}}>
            <NetSaveAI theme={theme} t={t} mobile={mobile} onClose={()=>setAiPanelOpen(false)}/>
          </div>
        </>
      )}

      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes slideIn{from{transform:translateX(-100%)}to{transform:translateX(0)}}
        *{box-sizing:border-box;-webkit-tap-highlight-color:transparent;}
        input,select,textarea{font-size:16px!important;}
        ::-webkit-scrollbar{width:4px;height:4px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:#334;border-radius:4px}
      `}</style>

      <div style={{display:"flex",height:"100vh",background:t.bg,fontFamily:"'Outfit',sans-serif",color:t.text,overflow:"hidden"}}>
        {/* Desktop sidebar */}
        {!mobile&&(
          <div style={{width:240,background:t.sidebarBg,borderRight:`1px solid ${t.border}`,display:"flex",flexDirection:"column",flexShrink:0}}>
            <SidebarContent/>
          </div>
        )}

        {/* Mobile drawer overlay */}
        {mobile&&sidebarOpen&&(
          <>
            <div onClick={()=>setSidebarOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:200,backdropFilter:"blur(2px)"}}/>
            <div style={{position:"fixed",top:0,left:0,bottom:0,width:280,background:t.sidebarBg,borderRight:`1px solid ${t.border}`,display:"flex",flexDirection:"column",zIndex:201,animation:"slideIn 0.22s ease"}}>
              <SidebarContent/>
            </div>
          </>
        )}

        {/* Main content */}
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minWidth:0}}>
          {/* Topbar */}
          <div style={{height:56,background:t.topbarBg,borderBottom:`1px solid ${t.border}`,display:"flex",alignItems:"center",padding:mobile?"0 14px":"0 28px",gap:12,flexShrink:0}}>
            {mobile&&(
              <button onClick={()=>setSidebarOpen(true)} style={{background:"none",border:"none",cursor:"pointer",padding:6,color:t.textSub,display:"flex",alignItems:"center"}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
              </button>
            )}
            {mobile
              ?<NetSaveLogo size={28} textMode theme={theme}/>
              :<div style={{fontSize:18,fontWeight:800,color:t.text,letterSpacing:"-0.4px"}}>{pageLabels[page]}</div>
            }
            <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
              {!mobile&&<>
                <a href="https://netsavetax.securefilepro.com/portal/#/login" target="_blank" rel="noopener noreferrer" style={{padding:"5px 12px",borderRadius:20,background:t.inputBg,border:"1px solid "+t.border,color:t.textSub,fontSize:11.5,fontWeight:600,textDecoration:"none",cursor:"pointer",fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap",transition:"all 0.12s"}}>Client Portal</a>
                <a href="https://www.netsavetax.com" target="_blank" rel="noopener noreferrer" style={{padding:"5px 12px",borderRadius:20,background:t.inputBg,border:"1px solid "+t.border,color:t.textSub,fontSize:11.5,fontWeight:600,textDecoration:"none",cursor:"pointer",fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap",transition:"all 0.12s"}}>Website</a>
                <a href="https://docuseal.com/" target="_blank" rel="noopener noreferrer" style={{padding:"5px 12px",borderRadius:20,background:t.inputBg,border:"1px solid "+t.border,color:t.textSub,fontSize:11.5,fontWeight:600,textDecoration:"none",cursor:"pointer",fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap",transition:"all 0.12s"}}>Docuseal</a>
                <a href="https://outlook.office365.com/host/4c4ec2e8-4a2c-4bce-8d8f-00fc664a4e5b/bookings" target="_blank" rel="noopener noreferrer" style={{padding:"5px 12px",borderRadius:20,background:t.inputBg,border:"1px solid "+t.border,color:t.textSub,fontSize:11.5,fontWeight:600,textDecoration:"none",cursor:"pointer",fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap",transition:"all 0.12s"}}>Appointments</a>
                <div style={{width:1,height:20,background:t.border,margin:"0 4px"}}/>
              </>}
              {/* Ask NetSave AI without leaving the page you are on. */}
              <button onClick={()=>setAiPanelOpen(true)} title="Ask NetSave AI"
                style={{display:"flex",alignItems:"center",gap:mobile?0:6,padding:mobile?"8px":"6px 12px",borderRadius:8,background:BRAND.blue+"15",border:`1px solid ${BRAND.blue}40`,cursor:"pointer",color:BRAND.blue,fontFamily:"'Outfit',sans-serif",flexShrink:0}}>
                <Icon name="brain" size={14} color={BRAND.blue}/>
                {!mobile&&<span style={{fontSize:12,fontWeight:700}}>AI</span>}
              </button>
              <div style={{position:"relative"}}>
                <button onClick={()=>setPage("pipeline")}
                  style={{display:"flex",alignItems:"center",justifyContent:"center",width:34,height:34,borderRadius:8,background:myPendingTasks.length>0?BRAND.blue+"15":t.inputBg,border:`1px solid ${myPendingTasks.length>0?BRAND.blue+"40":t.border}`,cursor:"pointer",position:"relative",flexShrink:0}}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={myPendingTasks.length>0?BRAND.blue:t.textSub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  {myPendingTasks.length>0&&(
                    <span style={{position:"absolute",top:-5,right:-5,background:"#f87171",color:"#fff",borderRadius:"50%",minWidth:17,height:17,fontSize:9,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",border:`2px solid ${t.bg}`,padding:"0 3px"}}>
                      {myPendingTasks.length}
                    </span>
                  )}
                </button>
              </div>
              <button onClick={toggleTheme} style={{display:"flex",alignItems:"center",gap:mobile?0:6,padding:mobile?"8px":"6px 12px",borderRadius:8,background:t.inputBg,border:`1px solid ${t.border}`,cursor:"pointer",color:t.textSub,fontFamily:"'Outfit',sans-serif"}}>
                <Icon name={theme==="dark"?"sun":"moon"} size={14} color={BRAND.blue}/>
                {!mobile&&<span style={{fontSize:12,fontWeight:600}}>{theme==="dark"?"Light":"Dark"}</span>}
              </button>
            </div>
          </div>

          {/* Page title bar on mobile */}
          {mobile&&(
            <div style={{padding:"10px 16px 8px",borderBottom:`1px solid ${t.border}`,background:t.surface}}>
              <div style={{fontSize:17,fontWeight:800,color:t.text,letterSpacing:"-0.3px"}}>{pageLabels[page]}</div>
            </div>
          )}

          {/* Content */}
          {/* The AI page manages its own scrolling in two panes, so it opts out
              of the padded, scrolling wrapper every other page relies on. */}
          <div style={page==="ai"
            ?{flex:1,overflow:"hidden",display:"flex",minHeight:0,paddingBottom:mobile?"62px":0}
            :{flex:1,overflowY:"auto",padding:mobile?"14px 12px":"24px 28px",paddingBottom:mobile?"80px":"24px"}}>
            {dataLoading
              ?<div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"calc(100vh - 120px)",gap:20}}>
                <div style={{display:"flex",gap:7,alignItems:"flex-end",height:48}}>
                  {[{c:BRAND.blue,d:"0s"},{c:BRAND.blue+"cc",d:"0.15s"},{c:BRAND.green,d:"0.3s"},{c:BRAND.green+"cc",d:"0.45s"},{c:BRAND.blue,d:"0.6s"}].map((b,i)=>(
                    <div key={i} style={{width:10,height:36,borderRadius:5,background:b.c,animation:"bounce 0.9s ease-in-out infinite",animationDelay:b.d}}/>
                  ))}
                </div>
                <div style={{fontSize:13,color:t.textMuted,fontWeight:500,letterSpacing:"0.04em"}}>Loading NetSave...</div>
              </div>
              :<>
                {page==="dashboard"&&<ReportsPage theme={theme} clients={clients}/>}
                {page==="pipeline"&&<PipelinePage clients={clients} leads={leads} theme={theme} mobile={mobile} staffSigs={staffSigs} sigSaving={sigSaving} saveSig={saveSig} clearSig={clearSig} currentUser={currentUser}
                  onNavigateToClient={c=>{setDashboardClient(c);setPage("clients");setTimeout(()=>setDashboardClient(null),100);}}
                  onNavigateToBiz={({biz,ownerClient})=>{setPipelineBiz({biz,ownerClient});setPage("clients");}}
                />}
                {page==="leads"&&<LeadsPage leads={leads} theme={theme} mobile={mobile}/>}
                {page==="clients"&&<ClientsPage clients={clients} theme={theme} mobile={mobile} initialViewClient={dashboardClient} initialViewBiz={pipelineBiz} onClearBiz={()=>setPipelineBiz(null)} staffSigs={staffSigs} currentUser={currentUser}/>}
                {page==="quickbooks"&&<QuickBooksPage clients={clients} theme={theme} mobile={mobile}/>}
                {page==="onedrive"&&<OneDrivePage clients={clients} theme={theme} mobile={mobile}/>}
                {page==="outlook"&&<OutlookPage clients={clients} theme={theme} mobile={mobile} authUser={authUser}/>}
                {page==="ai"&&<div style={{flex:1,minWidth:0,minHeight:0,display:"flex"}}><NetSaveAI theme={theme} t={t} mobile={mobile} fullPage/></div>}
                {page==="blog"&&<BlogPage theme={theme}/>}
                {page==="social"&&<SocialPage theme={theme}/>}
                {page==="settings"&&<SettingsPage theme={theme} staffSigs={staffSigs} sigSaving={sigSaving} saveSig={saveSig} clearSig={clearSig}/>}
              </>
            }
          </div>

          {/* Mobile bottom nav */}
          {mobile&&(
            <div style={{position:"fixed",bottom:0,left:0,right:0,background:t.sidebarBg,borderTop:`1px solid ${t.border}`,display:"flex",zIndex:100,paddingBottom:"env(safe-area-inset-bottom,0px)"}}>
              {nav.map(n=>{const active=page===n.id;return(
                <button key={n.id} onClick={()=>navigateTo(n.id)} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"10px 4px 8px",background:"none",border:"none",cursor:"pointer",color:active?BRAND.blue:t.textMuted,fontFamily:"'Outfit',sans-serif",position:"relative"}}>
                  <Icon name={n.icon} size={20} color={active?BRAND.blue:t.textMuted}/>
                  <span style={{fontSize:9.5,fontWeight:active?700:500,marginTop:3,letterSpacing:"0.02em"}}>{n.label}</span>
                  {n.badge?<span style={{position:"absolute",top:6,right:"50%",transform:"translateX(10px)",background:BRAND.blue,color:"#fff",fontSize:9,fontWeight:800,padding:"1px 5px",borderRadius:8,minWidth:16,textAlign:"center"}}>{n.badge}</span>:null}
                  {active&&<div style={{position:"absolute",bottom:0,left:"20%",right:"20%",height:2,background:BRAND.blue,borderRadius:2}}/>}
                </button>
              );})}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
